import D from "@/admin/dashboard/Dashboard.module.css";

import { HeadingBlock } from "@/admin/dashboard/HeadingBlock.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { PercentageChange } from "@/admin/dashboard/PercentageChange.tsx";
import { abbreviateNumber } from "@/admin/dashboard/utils.ts";
import { monthNames, WBarChart, type WBarChartOptions } from "@/admin/dashboard/WBarChart.tsx";
import {
    ColumnFilterInputType,
    DocumentType,
    InvoiceOrderFields,
    OrderDirection,
} from "@/admin/graphql.gen/graphql.ts";
import { GET_REVENUE_INVOICES } from "@/admin/users/revenueGraphql.ts";
import { BadgeDot } from "@/components/Badge.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { Loading } from "@/components/loading/Loading.tsx";
import { Separator } from "@/components/Separator.tsx";
import { ErrorMessage } from "@/modules/error/ErrorFallback.tsx";
import { formatDistanceAtom } from "@/modules/l10n/dateFnsLocale.ts";
import { useThemeVar } from "@/modules/theme/useTheme.ts";
import { useQuery } from "@apollo/client/react";
import { apiDatetimeToDate, apiUnixToDate } from "@warrenio/api-spec/conversion";
import { useAtomValue } from "jotai/react";
import { useNumberFormatter } from "react-aria";

interface RevenueSectionProps {
    userId?: string;
    billingAccountId?: string;
    createdAt?: string | number | null;
}

function RevenueSectionContent({ userId, billingAccountId, createdAt }: RevenueSectionProps) {
    const { data, error, loading } = useQuery(GET_REVENUE_INVOICES, {
        variables: {
            limit: 2000,
            orderField: InvoiceOrderFields.Created,
            orderDir: OrderDirection.Desc,
            filters: [
                {
                    field: userId ? InvoiceOrderFields.BillingAccountUserId : InvoiceOrderFields.BillingAccountId,
                    type: ColumnFilterInputType.Exact,
                    value: userId ? userId : billingAccountId,
                },
            ],
        },
    });

    const invoices = data == null ? undefined : (data.admin_invoice_list.items ?? []);
    const lifeTimeValue =
        invoices?.reduce(
            (total, invoice) =>
                total + (invoice.document_type === DocumentType.Invoice ? (invoice.totals?.total_before_tax ?? 0) : 0),
            0,
        ) ?? 0;
    const limitMonths = 12;

    const formatDistance = useAtomValue(formatDistanceAtom);
    const colorWhite = useThemeVar("color-white");
    const colorMuted = useThemeVar("color-muted");
    const colorTextHoverAlpha = useThemeVar("color-text-hover-alpha");
    const colorText = useThemeVar("color-text");
    const colorChart2 = useThemeVar("color-chart-2");
    const colorChart4 = useThemeVar("color-chart-4");
    const valueFormatter = useNumberFormatter({
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const invoiceTotalsByMonth = new Map<string, number>();
    const invoiceSubtotalsByMonth = new Map<string, number>();
    for (const invoice of invoices ?? []) {
        if (invoice.created == null) continue;

        const created = apiUnixToDate(invoice.created);
        const monthKey = `${created.getFullYear()}-${created.getMonth()}`;

        if (invoice.document_type === DocumentType.Invoice) {
            invoiceTotalsByMonth.set(
                monthKey,
                (invoiceTotalsByMonth.get(monthKey) ?? 0) + (invoice.totals?.total_before_tax ?? 0),
            );
        }

        if (invoice.type === "cloud_services") {
            invoiceSubtotalsByMonth.set(
                monthKey,
                (invoiceSubtotalsByMonth.get(monthKey) ?? 0) + (invoice.totals?.subtotal_resource_usage ?? 0),
            );
        }
    }

    const xAxisData: string[] = [];
    const monthlyInvoiceTotals: number[] = [];
    const monthlyInvoiceSubtotals: number[] = [];
    const now = new Date();
    const customerSince =
        createdAt == null
            ? "-"
            : formatDistance(
                  typeof createdAt === "number" ? apiUnixToDate(createdAt) : apiDatetimeToDate(createdAt),
                  now,
              );

    for (let iteratorMonths = limitMonths; iteratorMonths > 0; iteratorMonths--) {
        const generatedDate = new Date(now.getFullYear(), now.getMonth() - (iteratorMonths - 1), 1);
        const month = monthNames[generatedDate.getMonth()];
        const year = generatedDate.getFullYear();

        xAxisData.push(`${month} ${year}`);
        monthlyInvoiceTotals.push(invoiceTotalsByMonth.get(`${year}-${generatedDate.getMonth()}`) ?? 0);
        monthlyInvoiceSubtotals.push(invoiceSubtotalsByMonth.get(`${year}-${generatedDate.getMonth()}`) ?? 0);
    }

    const usageLastTotal = monthlyInvoiceTotals[limitMonths - 2] ?? 0;
    const usagePrevTotal = monthlyInvoiceTotals[limitMonths - 3] ?? 0;

    const options: WBarChartOptions = {
        tooltip: {
            trigger: "axis",
            valueFormatter: (value) => (typeof value === "number" ? valueFormatter.format(value) : String(value)),
            borderWidth: 0,
            backgroundColor: colorWhite,
            textStyle: {
                color: colorText,
            },
            extraCssText: "box-shadow: 0 8px 20px rgba(0 0 0 / 10%);",
        },
        grid: {
            top: "5px",
            left: "10px",
            right: "30px",
            bottom: "5px",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            boundaryGap: false,
            data: xAxisData,
            axisLine: {
                lineStyle: {
                    color: colorMuted,
                },
            },
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value) => abbreviateNumber(value),
            },
            splitLine: {
                lineStyle: {
                    color: colorTextHoverAlpha,
                },
            },
        },
        series: [
            {
                name: "Invoiced revenue (excl. VAT)",
                type: "line",
                data: monthlyInvoiceTotals,
                color: colorChart2,
            },
            {
                name: "Usage",
                type: "line",
                data: monthlyInvoiceSubtotals,
                color: colorChart4,
            },
        ],
    };

    if (error) return <ErrorMessage error={error} />;
    if (loading) return <Loading icon="none" fillSpace />;

    return (
        <>
            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Revenue</h2>
                    <p>Monthly invoiced revenue and usage over the past 12 months.</p>
                </div>

                <HeadingBlock title="Customer since/age" subtitle={<div>{customerSince}</div>} className="ml-auto" />

                <HeadingBlock
                    title="Last MoM growth"
                    subtitle={<CurrencyBalance value={usageLastTotal - usagePrevTotal} />}
                >
                    <PercentageChange last={usageLastTotal} prev={usagePrevTotal} />
                </HeadingBlock>

                <HeadingBlock
                    title="Lifetime value (excl. VAT)"
                    subtitle={
                        <div>
                            <CurrencyBalance value={lifeTimeValue} />
                        </div>
                    }
                />
            </div>

            <Separator />

            <div className="flex gap-1rem font-size-small pt-4 pb-4">
                <BadgeDot color="color-chart-2">Invoiced revenue (excl. VAT)</BadgeDot>
                <BadgeDot color="color-chart-4">Usage</BadgeDot>
            </div>

            <WBarChart options={options} />
        </>
    );
}

export function RevenueSection(props: RevenueSectionProps) {
    return (
        <LightBlock className={D.Wide} loadingHeight="460px">
            <RevenueSectionContent {...props} />
        </LightBlock>
    );
}
